import * as React from 'react';
import { CallbackWithRecommendationsWidget } from '@cian/callback-widget';
import { useDispatch, useSelector } from 'react-redux';

// eslint-disable-next-line import/no-restricted-paths
import { getWorkTimeSchedule } from '../../../browser/services/workTime';
import { closeRequestCallbackPopup } from '../../actions/requestCallback';
import { trackRecommendationsToggleOff, trackRecommendationsToggleOn } from '../../analytics/trackCallback';
import { getGaLabel, getUser } from '../../selectors/analytics';
import { getNewbuildingName, getTimezone } from '../../selectors/newbuilding';
import {
  getIsRequestCallbackOpen,
  getCallbackRecommendations,
  getRequestCallbackOpener,
} from '../../selectors/requestCallback';
import { getUserPhone } from '../../selectors/user';
import { getAgentForCallback } from '../../selectors/newbuilding/agent';
import { TAgent } from '../../types/agent';
import { IRequestCallbackParams, TRequestCallbackOpener } from '../../types/requestCallback';
import { useApplicationContext } from '../../utils/applicationContext';
import { getPhoneString } from '../../utils/phone';
import { getPrivacyPolicyUrl, getSmsDisclaimerState, requestCallbackWithRecommendations } from './helpers';

export function CallbackPopupContainer() {
  const { config } = useApplicationContext();

  const isPopupOpened = useSelector(getIsRequestCallbackOpen);
  const opener = useSelector(getRequestCallbackOpener);
  const agent = useSelector(getAgentForCallback);
  const gaLabel = useSelector(getGaLabel);
  const user = useSelector(getUser);
  const userPhone = useSelector(getUserPhone);
  const timezone = useSelector(getTimezone);
  const newbuildingName = useSelector(getNewbuildingName);
  const recommendations = useSelector(getCallbackRecommendations);
  const dispatch = useDispatch();

  const callbackPhone = agent ? getPhoneString(agent.phone) : null;
  const privacyPolicyUrl = getPrivacyPolicyUrl(config);
  const isSMSDisclaimerEnabled = getSmsDisclaimerState(config);

  const onClose = React.useCallback(() => {
    dispatch(closeRequestCallbackPopup());
  }, [dispatch]);

  const onRequestCallback = React.useCallback(
    async (parameters: IRequestCallbackParams) => {
      const result = await requestCallbackWithRecommendations(parameters, {
        analyticsLabel: gaLabel,
        placeType: opener as TRequestCallbackOpener,
        user,
      });

      return result;
    },
    [gaLabel, user, opener],
  );

  const handleRecommendationsChange = React.useCallback((status: boolean) => {
    if (status) {
      trackRecommendationsToggleOn();
    } else {
      trackRecommendationsToggleOff();
    }
  }, []);

  const handleRequestWorkTime = React.useCallback(async () => {
    const userId = (agent as TAgent).realtyUserId;
    const result = await getWorkTimeSchedule({ userId });

    return result;
  }, [agent]);

  if (!callbackPhone) {
    return null;
  }

  return (
    <CallbackWithRecommendationsWidget
      agentPhone={callbackPhone}
      onClose={onClose}
      newbuildingName={newbuildingName}
      onRequestCallback={onRequestCallback}
      onRequestWorkTime={handleRequestWorkTime}
      onRecommendationsChange={handleRecommendationsChange}
      open={isPopupOpened}
      privacyPolicyUrl={privacyPolicyUrl}
      recommendations={recommendations}
      timezoneOffset={timezone}
      userPhoneNumber={userPhone}
      isSMSDisclaimerEnabled={isSMSDisclaimerEnabled}
    />
  );
}
